import React, { useState, useEffect, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { Spin, Button, message } from 'antd';
import { LoadingOutlined, FileExcelOutlined, PrinterOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import moment from 'moment';

const antIcon = <LoadingOutlined style={{ fontSize: 40 }} spin />;

const SamplesChart = ({ delivered_to_hubs_list, datarequested }) => {
  const chartRef = useRef(null);

  // Excel export functionality
  const exportToExcel = () => {
    try {
      if (!delivered_to_hubs_list || delivered_to_hubs_list.length === 0) {
        message.warning('No data available to export');
        return;
      }

      // Prepare data for Excel
      const excelData = [];
      
      // Add header row
      const sampleTypes = new Set();
      delivered_to_hubs_list.forEach(monthData => {
        if (monthData.samples) {
          monthData.samples.forEach(sample => {
            sampleTypes.add(sample.sample_name);
          });
        }
      });
      
      const sortedSampleTypes = Array.from(sampleTypes).sort();
      
      // Create Excel data
      delivered_to_hubs_list.forEach(monthData => {
        const row = { Month: monthData.month };
        sortedSampleTypes.forEach(sampleType => {
          const sampleData = monthData.samples && monthData.samples.find(s => s.sample_name === sampleType);
          row[sampleType.toUpperCase()] = sampleData ? sampleData.total_in_month : 0;
        });
        row['MONTHLY TOTAL'] = monthData.monthly_total || 0;
        excelData.push(row);
      });

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Samples Delivered Data');
      
      // Generate filename
      const filename = `Samples_Delivered_Report_${moment().format('DD_MM_YYYY')}.xlsx`;
      
      // Write and download file
      XLSX.writeFile(wb, filename);
      
      message.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export data. Please try again.');
    }
  };

  // Image download functionality
  const downloadAsImage = () => {
    try {
      if (chartRef.current) {
        let canvas = null;
        
        // Try multiple ways to access the canvas (Chart.js v2 compatibility)
        if (chartRef.current.chartInstance && chartRef.current.chartInstance.canvas) {
          canvas = chartRef.current.chartInstance.canvas;
        } else if (chartRef.current.chart && chartRef.current.chart.canvas) {
          canvas = chartRef.current.chart.canvas;
        } else if (chartRef.current.canvas) {
          canvas = chartRef.current.canvas;
        } else {
          // Try to find canvas element directly
          const chartContainer = chartRef.current;
          if (chartContainer && chartContainer.querySelector) {
            canvas = chartContainer.querySelector('canvas');
          }
        }
        
        if (canvas) {
          // Use canvas toDataURL method directly
          const url = canvas.toDataURL('image/png', 1.0);
          
          // Create download link
          const link = document.createElement('a');
          link.download = `Samples_Delivered_Chart_${moment().format('DD_MM_YYYY')}.png`;
          link.href = url;
          
          // Trigger download
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          message.success('Chart image downloaded successfully!');
        } else {
          throw new Error('Chart canvas not accessible');
        }
      } else {
        throw new Error('Chart reference not available');
      }
    } catch (error) {
      console.error('Image download error:', error);
      message.error('Failed to download chart image. Please try again.');
    }
  };

  // Generate dynamic colors for sample types
  const generateDynamicColor = (index, alpha = 0.8) => {
    // Base color palette - professional and distinct colors
    const baseColors = [
      [255, 99, 132],   // Red
      [54, 162, 235],   // Blue
      [255, 206, 86],   // Yellow
      [75, 192, 192],   // Teal
      [153, 102, 255],  // Purple
      [255, 159, 64],   // Orange
      [199, 199, 199],  // Gray
      [83, 102, 255],   // Indigo
      [255, 99, 255],   // Magenta
      [99, 255, 132],   // Light Green
      [255, 159, 243],  // Pink
      [132, 99, 255],   // Light Purple
      [255, 206, 132],  // Light Orange
      [99, 206, 255],   // Light Blue
      [206, 255, 99],   // Lime
      [255, 132, 99],   // Coral
      [132, 255, 206],  // Mint
      [206, 132, 255],  // Lavender
      [255, 255, 99],   // Light Yellow
      [99, 132, 255]    // Periwinkle
    ];

    // If we have more sample types than base colors, generate additional colors
    if (index < baseColors.length) {
      const [r, g, b] = baseColors[index];
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    } else {
      // Generate additional colors using HSL for better distribution
      const hue = (index * 137.508) % 360; // Golden angle approximation for good distribution
      const saturation = 70 + (index % 3) * 10; // Vary saturation slightly
      const lightness = 50 + (index % 4) * 5;   // Vary lightness slightly
      
      // Convert HSL to RGB
      const hslToRgb = (h, s, l) => {
        h /= 360;
        s /= 100;
        l /= 100;
        
        const a = s * Math.min(l, 1 - l);
        const f = n => {
          const k = (n + h * 12) % 12;
          return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        };
        
        return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
      };
      
      const [r, g, b] = hslToRgb(hue, saturation, lightness);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  };

  // Function to get colors for sample types
  const getSampleTypeColors = (sampleTypes) => {
    const colors = {};
    const borderColors = {};
    
    sampleTypes.forEach((sampleType, index) => {
      colors[sampleType] = generateDynamicColor(index, 0.8);
      borderColors[sampleType] = generateDynamicColor(index, 1);
    });
    
    return { colors, borderColors };
  };

  const prepareChartData = () => {
    if (!delivered_to_hubs_list || delivered_to_hubs_list.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    // Extract months for x-axis
    const months = delivered_to_hubs_list.map(item => item.month);

    // Get all unique sample types
    const allSampleTypes = new Set();
    delivered_to_hubs_list.forEach(monthData => {
      if (monthData.samples) {
        monthData.samples.forEach(sample => {
          allSampleTypes.add(sample.sample_name);
        });
      }
    });

    // Convert to array and sort for consistent ordering
    const sampleTypesArray = Array.from(allSampleTypes).sort();
    
    // Generate colors for all sample types
    const { colors: sampleColors, borderColors: sampleBorderColors } = getSampleTypeColors(sampleTypesArray);

    // Create datasets for each sample type
    const datasets = sampleTypesArray.map(sampleType => {
      const data = delivered_to_hubs_list.map(monthData => {
        if (monthData.samples) {
          const sampleData = monthData.samples.find(sample => sample.sample_name === sampleType);
          return sampleData ? sampleData.total_in_month : 0;
        }
        return 0;
      });

      return {
        label: sampleType.toUpperCase(),
        data: data,
        backgroundColor: sampleColors[sampleType],
        borderColor: sampleBorderColors[sampleType],
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
      };
    });

    return {
      labels: months,
      datasets: datasets
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: 'VOLUME OF SAMPLES DELIVERED AT HUBS',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 30
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: function(context) {
            return `Month: ${context[0].label}`;
          },
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y} samples`;
          },
          footer: function(tooltipItems) {
            let total = 0;
            tooltipItems.forEach(function(tooltipItem) {
              total += tooltipItem.parsed.y;
            });
            return `Total: ${total} samples`;
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'MONTH',
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12,
            weight: '500'
          }
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'TOTAL NUMBER OF SAMPLES',
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 1
        },
        ticks: {
          beginAtZero: true,
          stepSize: 1,
          font: {
            size: 12,
            weight: '500'
          },
          callback: function(value, index, values) {
            return Number.isInteger(value) ? value : '';
          }
        }
      }
    },
    elements: {
      bar: {
        borderWidth: 1,
      }
    }
  };

  if (datarequested === true) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" indicator={antIcon}>
          <div className="content">
            <h3>Loading Samples Data...</h3>
          </div>
        </Spin>
      </div>
    );
  }

  const chartData = prepareChartData();

  if (chartData.labels.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div className="content">
          <h3>No samples data available</h3>
          <p>Please adjust your filters and try again</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animated fadeIn" style={{ padding: '20px' }}>
      
      {/* Action Buttons */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        
        {/* Excel Download Button */}
        <Button 
          type="primary" 
          icon={<FileExcelOutlined />}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          onClick={exportToExcel}
        >
          Download Excel
        </Button>

        {/* Image Download Button */}
        <Button 
          type="primary" 
          icon={<DownloadOutlined />}
          style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
          onClick={downloadAsImage}
        >
          Download Image
        </Button>

      </div>

      {/* Chart Container */}
      <div style={{ height: '500px' }}>
        <Bar 
          ref={chartRef} 
          data={chartData} 
          options={chartOptions}
        />
      </div>
      
    </div>
  );
};

export default SamplesChart; 